/** @jsxImportSource @emotion/react */
import Button from "@mui/material/Button";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import { useState } from "react";
import { toast } from "react-toastify";
import {
  downloadCampaignTicket,
  sendCampaignTicketEmail,
} from "../../../api/campaignApi";
import t from "../../../theme";
import { formatCurrency, getCampaignTitle } from "../utils";

const findTicketUuid = (value, seen = new Set()) => {
  if (!value || typeof value !== "object" || seen.has(value)) return "";
  seen.add(value);

  if (value.ticket_uuid) return value.ticket_uuid;
  if (value.ticketUuid) return value.ticketUuid;
  if (
    value.uuid &&
    (value.status === "booked" ||
      value.status === "reserved" ||
      value.pdf_url ||
      value.original_filename ||
      value.booked_at)
  ) {
    return value.uuid;
  }

  for (const nestedValue of Object.values(value)) {
    const nestedTicketUuid = findTicketUuid(nestedValue, seen);
    if (nestedTicketUuid) return nestedTicketUuid;
  }

  return "";
};

const getTicketUuid = (campaign = {}) => findTicketUuid(campaign);

const styles = {
  panel: {
    marginTop: 16,
    borderRadius: 16,
    overflow: "hidden",
    border: `1px solid ${t.green.border}`,
    background: "rgba(236, 253, 243, 0.38)",
    backdropFilter: t.blur.medium,
    WebkitBackdropFilter: t.blur.medium,
    boxShadow: "none",
    fontFamily: t.font.family,
  },
  header: {
    padding: "14px 16px 0",
    color: "#101828",
    background: "transparent",
  },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    borderRadius: 999,
    padding: "5px 9px",
    background: t.green.bgSelected,
    color: t.green.dark,
    fontSize: "0.72rem",
    fontWeight: 700,
  },
  headerTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },
  amountPaid: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    color: "rgba(71, 84, 103, 0.62)",
    fontSize: "0.76rem",
    fontWeight: 800,
    "& strong": {
      color: "rgba(45, 122, 21, 0.68)",
      fontSize: "0.95rem",
      fontWeight: 900,
    },
  },
  title: {
    margin: "10px 0 5px",
    fontSize: "1rem",
    lineHeight: 1.2,
    fontWeight: 700,
  },
  copy: {
    margin: 0,
    color: "#475467",
    fontSize: "0.82rem",
    lineHeight: 1.45,
  },
  body: {
    padding: 16,
    display: "grid",
    gap: 12,
  },
  actions: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 10,
    "@media (max-width: 520px)": {
      gridTemplateColumns: "minmax(0, 1fr)",
    },
  },
  button: {
    borderRadius: 999,
    textTransform: "none",
    fontWeight: 700,
    minHeight: 42,
    "&.MuiButton-contained": {
      background: t.green.gradient,
      color: t.brand.white,
      boxShadow: t.shadow.brand,
      "&:hover": {
        background: "linear-gradient(135deg, #4a9b2e 0%, #2d7a15 100%)",
        boxShadow: t.shadow.brandFocus,
      },
    },
    "&.MuiButton-outlined": {
      color: t.green.dark,
      borderColor: t.green.borderSelected,
      background: t.green.bg,
      "&:hover": {
        borderColor: t.green.solid,
        background: t.green.bgHover,
      },
    },
  },
};

const saveBlob = ({ blob, contentType, filename }) => {
  const fileBlob = blob.type ? blob : new Blob([blob], { type: contentType });
  const url = URL.createObjectURL(fileBlob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename || "campaign-ticket.pdf";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const openTicketFile = (file) => {
  if (file?.url) {
    window.open(file.url, "_blank", "noopener,noreferrer");
    return;
  }

  if (file?.blob) {
    saveBlob(file);
    return;
  }

  throw new Error("Ticket file was not returned by the backend.");
};

const BookedTicketPanel = ({ campaign, token }) => {
  const ticketUuid = getTicketUuid(campaign);
  const amountPaid = formatCurrency(campaign.discounted_price);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isEmailing, setIsEmailing] = useState(false);

  const downloadTicket = async () => {
    if (!ticketUuid) {
      toast.error("Ticket is not ready for download yet.");
      return;
    }

    try {
      setIsDownloading(true);
      const file = await downloadCampaignTicket({ ticketUuid, token });
      openTicketFile(file);
    } catch (error) {
      toast.error(error?.message || "Unable to download ticket.");
    } finally {
      setIsDownloading(false);
    }
  };

  const emailTicket = async () => {
    if (!ticketUuid) {
      toast.error("Ticket is not ready to email yet.");
      return;
    }

    try {
      setIsEmailing(true);
      const result = await sendCampaignTicketEmail({ ticketUuid, token });
      toast.success(result?.message || "Ticket email sent.");
    } catch (error) {
      toast.error(error?.message || "Unable to email ticket.");
    } finally {
      setIsEmailing(false);
    }
  };

  return (
    <section css={styles.panel}>
      <div css={styles.header}>
        <div css={styles.headerTop}>
          <span css={styles.badge}>
            <VerifiedOutlinedIcon sx={{ fontSize: 16 }} />
            Booked Ticket
          </span>
          {amountPaid && (
            <span css={styles.amountPaid}>
              Amount Paid: <strong>{amountPaid}</strong>
            </span>
          )}
        </div>
        <h3 css={styles.title}>{getCampaignTitle(campaign)}</h3>
        <p css={styles.copy}>
          This ticket is booked. You cannot claim another ticket in this campaign.
        </p>
      </div>
      <div css={styles.body}>
        <div css={styles.actions}>
          <Button
            css={styles.button}
            variant="contained"
            startIcon={<DownloadOutlinedIcon />}
            onClick={downloadTicket}
            disabled={isDownloading}
          >
            {isDownloading ? "Downloading..." : "Download Ticket"}
          </Button>
          <Button
            css={styles.button}
            variant="outlined"
            startIcon={<EmailOutlinedIcon />}
            onClick={emailTicket}
            disabled={isEmailing}
          >
            {isEmailing ? "Sending..." : "Email Ticket"}
          </Button>
        </div>
      </div>
    </section>
  );
};

export default BookedTicketPanel;
